// src/modules/chat/chatStore.js
// ─────────────────────────────────────────────────────────────────────────────
// Central data layer — agents, Ollama streaming, platform context
//
// BRIDGE: LocalAgents Config Studio writes to localStorage('agentConfigs').
//   chatStore reads that to resolve: entity → ollama model + system prompt.
//   If an agent is configured with a brain, comingSoon = false and it's live.
// ─────────────────────────────────────────────────────────────────────────────

export const ENTITY_TYPES = {
  MY_AI:     'my_ai',
  PUBLIC_AI: 'public_ai',
  HUMAN:     'human',
};

// ── Base entity definitions (static metadata) ────────────────────────────────
// These IDs match LocalAgents AGENT_TEMPLATES IDs exactly — that's the bridge.
const BASE_ENTITIES = {
  'data_scientist': {
    id: 'data_scientist',
    name: 'Data Scientist Agent',
    handle: '@data_scientist',
    role: 'EDA · Feature Engineering · Model Evaluation',
    type: ENTITY_TYPES.MY_AI,
    avatar: 'DS',
    color: '#22d3ee',
    online: true,
    description: 'Full-stack data science: profiling, cleaning, feature engineering, model selection, evaluation. Thinks in distributions, not just means.',
    capabilities: ['schema profiling', 'feature engineering', 'model evaluation', 'distribution analysis', 'quality audit'],
    quickPrompts: [
      'What quality issues does my current dataset have?',
      'Which features should I engineer first?',
      'What ML algorithm would you start with here?',
      'Summarise the key findings in plain English',
    ],
    defaultSystemPrompt: `You are a Data Scientist agent on Jorvex.
You help researchers with end-to-end data science: profiling datasets, detecting quality issues, 
engineering features, selecting models, and evaluating results.
Be rigorous and specific. Always distinguish correlation from causation.
When you see dataset context, reason about it specifically — never generically.
Keep answers concise. Use markdown code blocks for any Python snippets.`,
    defaultModel: null,
  },

  /* ── Additional agents — coming soon, uncomment to enable ─────────────────

  'business_analyst': {
    id: 'business_analyst',
    name: 'Business Analyst Agent',
    handle: '@business_analyst',
    role: 'KPI Analysis · Business Signals · Decision Intelligence',
    type: ENTITY_TYPES.MY_AI,
    avatar: 'BA',
    color: '#a78bfa',
    online: true,
    description: 'Bridges raw data and executive decisions. Translates statistical outputs into plain business language, identifies growth levers, and flags risk signals.',
    capabilities: ['kpi interpretation', 'executive summaries', 'trend narrative', 'stakeholder framing', 'cohort analysis'],
    quickPrompts: [
      'Turn these findings into business impact',
      'Draft an executive summary of the analysis',
      'What are the key risk signals here?',
      'Which KPIs should I prioritise?',
    ],
    defaultSystemPrompt: `You are a Business Analyst agent on Jorvex.
You translate raw data and ML findings into clear business language: KPI impact, 
growth levers, risk signals, and executive-ready summaries.
Always connect data to business outcomes. Quantify impact in percentages or dollar terms where possible.
Avoid jargon. Write for non-technical stakeholders.`,
    defaultModel: null,
  },

  'ml_researcher': {
    id: 'ml_researcher',
    name: 'ML Researcher Agent',
    handle: '@ml_researcher',
    role: 'Experiment Design · Model Comparison · Training Diagnosis',
    type: ENTITY_TYPES.MY_AI,
    avatar: 'ML',
    color: '#4ade80',
    online: true,
    description: 'Designs end-to-end ML systems. Goes beyond model selection — architecture, data pipelines, hyperparameter search, evaluation frameworks, and deployment.',
    capabilities: ['experiment planning', 'model comparison', 'overfit diagnosis', 'hyperparameter strategy', 'mlops planning'],
    quickPrompts: [
      'Compare model paths for this task',
      'Diagnose overfitting in my experiment',
      'Plan the next experiment step',
      'What hyperparameters should I tune first?',
    ],
    defaultSystemPrompt: `You are an ML Researcher agent on Jorvex.
You help with experiment design, model comparison, training diagnosis, and ML system architecture.
Think in production systems, not just notebooks. Always consider reproducibility.
Give specific, actionable advice. Use chain-of-thought for complex decisions.`,
    defaultModel: null,
  },

  'vision_agent': {
    id: 'vision_agent',
    name: 'Vision Agent',
    handle: '@vision_agent',
    role: 'Image Analysis · Object Detection · CV Pipelines',
    type: ENTITY_TYPES.MY_AI,
    avatar: 'VI',
    color: '#f59e0b',
    online: true,
    description: 'Specialises in visual data. Analyses image datasets, designs CV pipelines, recommends architectures, and handles classification through semantic segmentation.',
    capabilities: ['image inspection', 'architecture selection', 'augmentation strategy', 'ocr guidance', 'visual anomaly detection'],
    quickPrompts: [
      'Recommend a CV architecture for this task',
      'What augmentation strategy should I use?',
      'Inspect this image dataset for issues',
      'Compare CNN vs ViT for my use case',
    ],
    defaultSystemPrompt: `You are a Vision Agent on Jorvex.
You specialise in computer vision: image analysis, CV architecture design, 
augmentation strategies, transfer learning, and visual data pipelines.
Recommend transfer learning over training from scratch unless justified.
Be pragmatic about compute constraints.`,
    defaultModel: null,
  },

  'paper_eda_agent': {
    id: 'paper_eda_agent',
    name: 'Research Paper Agent',
    handle: '@paper_eda_agent',
    role: 'Literature Synthesis · Hypothesis Generation · Paper Breakdown',
    type: ENTITY_TYPES.MY_AI,
    avatar: 'PE',
    color: '#ec4899',
    online: true,
    description: 'Accelerates research cycles. Synthesises papers, surfaces contradictions, generates ranked hypotheses, designs experiments, and structures findings for publication.',
    capabilities: ['paper decomposition', 'claim extraction', 'hypothesis generation', 'experiment design', 'literature synthesis'],
    quickPrompts: [
      'Break down this paper into key claims',
      'Generate 3 testable hypotheses from this finding',
      'What are the limitations of this approach?',
      'How does this relate to existing literature?',
    ],
    defaultSystemPrompt: `You are a Research Paper Agent on Jorvex.
You help with literature synthesis, paper decomposition, hypothesis generation, and experiment design.
Be intellectually rigorous. Actively look for evidence that contradicts the hypothesis.
Always ask: "what would change my mind?" Generate testable, falsifiable hypotheses.`,
    defaultModel: null,
  },

  'verification_agent': {
    id: 'verification_agent',
    name: 'Verification Agent',
    handle: '@verification_agent',
    role: 'Research Integrity · Reproducibility · Audit Trails',
    type: ENTITY_TYPES.PUBLIC_AI,
    avatar: 'VA',
    color: '#fbbf24',
    online: true,
    description: 'Platform-wide integrity layer. Helps verify reproducibility, check experimental design validity, audit statistical claims, and flag methodological issues.',
    capabilities: ['reproducibility checks', 'statistical validity', 'methodology audit', 'integrity proofs', 'claim verification'],
    quickPrompts: [
      'Check this experiment for reproducibility issues',
      'Audit the statistical claims in this analysis',
      'Are there p-hacking risks in this design?',
      'Verify the methodology is sound',
    ],
    defaultSystemPrompt: `You are a Verification Agent on Jorvex.
You help verify reproducibility, audit statistical claims, check experimental validity, and flag methodological issues.
Be a constructive critic. Flag issues clearly with severity levels (Critical/Major/Minor).
Suggest concrete fixes for every issue you raise.`,
    defaultModel: null,
  },

  'open_research_agent': {
    id: 'open_research_agent',
    name: 'Open Research Agent',
    handle: '@open_research_agent',
    role: 'Publishing · Open Results · Community Synthesis',
    type: ENTITY_TYPES.PUBLIC_AI,
    avatar: 'OR',
    color: '#f87171',
    online: true,
    description: 'Publishing and open-results layer. Prepares findings for publication, drafts public summaries, structures research for community consumption, and packages results.',
    capabilities: ['publish drafts', 'public summaries', 'result packaging', 'community framing', 'citation preparation'],
    quickPrompts: [
      'Prepare this finding for public sharing',
      'Draft a community-friendly summary',
      'Structure this as a research report',
      'What should I include in the methods section?',
    ],
    defaultSystemPrompt: `You are an Open Research Agent on Jorvex.
You help researchers prepare findings for publication and public sharing.
Write clearly for broad scientific audiences. Structure outputs with standard research conventions.
Help make results reproducible, well-documented, and community-ready.`,
    defaultModel: null,
  },

  // ── Agents matching LocalAgents panel IDs exactly ─────────────────────────
  // These IDs must stay in sync with the AGENTS array in LocalAgents.jsx so
  // localStorage('agentConfigs') keys resolve correctly in getEntity().

  'eda': {
    id: 'eda',
    name: 'Data Analyst',
    handle: '@data_analyst',
    role: 'Auto EDA · Pattern Detection · Quality Audit',
    type: ENTITY_TYPES.MY_AI,
    avatar: '🔬',
    color: '#22d3ee',
    online: true,
    description: 'Profiles datasets end-to-end — detects nulls, outliers, distributions, correlations. Flags data quality issues and writes plain-English summaries ready to drop into reports.',
    capabilities: ['schema profiling', 'null detection', 'outlier analysis', 'correlation analysis', 'quality audit'],
    quickPrompts: [
      'What quality issues does my dataset have?',
      'Show me the top correlations',
      'Profile this dataset end-to-end',
      'Write an EDA summary for my report',
    ],
    defaultSystemPrompt: `You are a Data Analyst agent on Jorvex.
You perform automated EDA: profiling datasets, detecting nulls, outliers, distribution shapes, and correlations.
Be specific and quantitative. Flag the top 3 action items in every analysis.
Write clearly — your summaries go directly into research reports.`,
    defaultModel: null,
  },

  'ml': {
    id: 'ml',
    name: 'ML Planner',
    handle: '@ml_planner',
    role: 'Model Selection · Hyperparameter Plans · Training Strategy',
    type: ENTITY_TYPES.MY_AI,
    avatar: '⚙️',
    color: '#a78bfa',
    online: true,
    description: 'Recommends ML algorithms, hyperparameter starting points, validation strategy, and step-by-step training plans with success metrics.',
    capabilities: ['algorithm selection', 'hyperparameter tuning', 'cross-validation design', 'training strategy', 'failure mode analysis'],
    quickPrompts: [
      'What ML algorithm should I use for this task?',
      'Suggest hyperparameter ranges to start with',
      'Design a cross-validation strategy for my data',
      'What failure modes should I watch for?',
    ],
    defaultSystemPrompt: `You are an ML Planner agent on Jorvex.
You help with machine learning planning: algorithm selection, hyperparameter strategy, cross-validation design, and training plans.
Think in production systems, not just notebooks. Always consider reproducibility and compute constraints.
Give specific, actionable advice with concrete starting points.`,
    defaultModel: null,
  },

  'report': {
    id: 'report',
    name: 'Report Writer',
    handle: '@report_writer',
    role: 'Structured Reports · Summaries · Technical Docs',
    type: ENTITY_TYPES.MY_AI,
    avatar: '📝',
    color: '#34d399',
    online: true,
    description: 'Takes raw analysis results and writes polished, structured reports — from one-page executive summaries to detailed technical appendices. Supports Markdown output.',
    capabilities: ['executive summaries', 'technical reports', 'markdown output', 'structured writing', 'stakeholder docs'],
    quickPrompts: [
      'Write an executive summary of these findings',
      'Turn this analysis into a structured report',
      'Draft a methods section for this experiment',
      'Write a conclusion for stakeholders',
    ],
    defaultSystemPrompt: `You are a Report Writer agent on Jorvex.
You write polished, structured reports from raw analysis results.
Formats: executive brief, full structured report, technical appendix, or one-pagers.
Always include: key findings, methodology summary, and actionable recommendations.
Tone: professional but accessible. Use markdown headers and tables where helpful.`,
    defaultModel: null,
  },

  'research': {
    id: 'research',
    name: 'Research Assistant',
    handle: '@research_helper',
    role: 'Paper Summaries · Hypothesis Generation · Literature Context',
    type: ENTITY_TYPES.MY_AI,
    avatar: '🧪',
    color: '#f59e0b',
    online: true,
    description: 'Accelerates research workflows. Summarises papers, generates hypotheses from data patterns, suggests related work, and helps plan experiments.',
    capabilities: ['paper summarisation', 'hypothesis generation', 'literature synthesis', 'experiment planning', 'claim extraction'],
    quickPrompts: [
      'Break down this paper into key claims',
      'Generate 3 testable hypotheses from this finding',
      'What are the limitations of this approach?',
      'How does this relate to existing literature?',
    ],
    defaultSystemPrompt: `You are a Research Assistant agent on Jorvex.
You accelerate research cycles: summarising papers, generating hypotheses, synthesising literature, and designing experiments.
Be intellectually rigorous. Always ask "what would change my mind?"
Generate testable, falsifiable hypotheses with clear reasoning chains.`,
    defaultModel: null,
  },

  'code': {
    id: 'code',
    name: 'Code Reviewer',
    handle: '@code_reviewer',
    role: 'Code Review · Bug Detection · Optimisation',
    type: ENTITY_TYPES.MY_AI,
    avatar: '💻',
    color: '#60a5fa',
    online: true,
    description: 'Reviews data science and ML code. Spots logical bugs, performance bottlenecks, and anti-patterns. Suggests better libraries and cleaner implementations.',
    capabilities: ['bug detection', 'performance review', 'code quality', 'ML best practices', 'refactoring suggestions'],
    quickPrompts: [
      'Review this code for bugs',
      'How can I make this more efficient?',
      'Are there any ML anti-patterns here?',
      'Suggest a cleaner implementation',
    ],
    defaultSystemPrompt: `You are a Code Reviewer agent on Jorvex.
You review data science and ML code for correctness, performance, readability, and best practices.
Rate issues as Critical / Major / Minor. For Critical and Major issues, provide a corrected code snippet.
Check for: data leakage, off-by-one errors, numerical instability, memory inefficiency, API misuse.`,
    defaultModel: null,
  },

  'insight': {
    id: 'insight',
    name: 'Insight Finder',
    handle: '@insight_finder',
    role: 'Non-Obvious Patterns · Anomaly Detection · Business Signals',
    type: ENTITY_TYPES.MY_AI,
    avatar: '🎯',
    color: '#f472b6',
    online: true,
    description: 'Goes beyond standard EDA. Surfaces non-obvious cross-column interactions, temporal anomalies, hidden segments, and translates statistical signals into plain business language.',
    capabilities: ['pattern detection', 'cross-column interactions', 'anomaly hunting', 'segment analysis', 'business translation'],
    quickPrompts: [
      'What is the most surprising finding here?',
      'Find non-obvious patterns in this data',
      'Are there hidden segments or clusters?',
      'Translate these statistics into business impact',
    ],
    defaultSystemPrompt: `You are an Insight Finder agent on Jorvex.
You surface non-obvious patterns: interaction effects, structural breaks, anomalies, hidden sub-populations.
Be contrarian — explicitly look for findings that would surprise a domain expert.
Rate each insight by Novelty (1-5) and Actionability (1-5). Always translate statistical findings into business language.`,
    defaultModel: null,
  },

  'business_analyst': {
    id: 'business_analyst',
    name: 'Business Analyst',
    handle: '@biz_analyst',
    role: 'KPI Analysis · Business Signals · Decision Intelligence',
    type: ENTITY_TYPES.MY_AI,
    avatar: '📊',
    color: '#f59e0b',
    online: true,
    description: 'Bridges raw data and executive decisions. Translates statistical outputs into plain business language, identifies growth levers, flags risk signals.',
    capabilities: ['kpi interpretation', 'executive summaries', 'trend narrative', 'stakeholder framing', 'cohort analysis'],
    quickPrompts: [
      'Turn these findings into business impact',
      'Draft an executive summary',
      'What are the key risk signals here?',
      'Which KPIs should I prioritise?',
    ],
    defaultSystemPrompt: `You are a Business Analyst agent on Jorvex.
You translate raw data and ML findings into clear business language: KPI impact, growth levers, risk signals, and executive-ready summaries.
Always connect data to business outcomes. Quantify impact in percentages or dollar terms where possible.
Avoid jargon. Write for non-technical stakeholders.`,
    defaultModel: null,
  },

  'ml_researcher': {
    id: 'ml_researcher',
    name: 'ML Researcher',
    handle: '@ml_researcher',
    role: 'Architecture Design · Training Strategy · MLOps Planning',
    type: ENTITY_TYPES.MY_AI,
    avatar: '⚙️',
    color: '#a78bfa',
    online: true,
    description: 'Designs end-to-end ML systems. Beyond model selection — data pipelines, hyperparameter search, evaluation frameworks, and deployment architectures.',
    capabilities: ['experiment planning', 'model comparison', 'overfit diagnosis', 'hyperparameter strategy', 'mlops planning'],
    quickPrompts: [
      'Compare model paths for this task',
      'Diagnose overfitting in my experiment',
      'Plan the next experiment step',
      'What hyperparameters should I tune first?',
    ],
    defaultSystemPrompt: `You are an ML Researcher agent on Jorvex.
You help with experiment design, model comparison, training diagnosis, and ML system architecture.
Think in production systems, not just notebooks. Always consider reproducibility.
Give specific, actionable advice. Use chain-of-thought for complex decisions.`,
    defaultModel: null,
  },

  'paper_eda_agent': {
    id: 'paper_eda_agent',
    name: 'Research Paper Agent',
    handle: '@paper_agent',
    role: 'Literature Synthesis · Hypothesis Generation · Experiment Design',
    type: ENTITY_TYPES.MY_AI,
    avatar: '🧪',
    color: '#ec4899',
    online: true,
    description: 'Accelerates the full research cycle. Synthesises papers, surfaces contradictions, generates ranked hypotheses, and designs statistically sound experiments.',
    capabilities: ['paper decomposition', 'claim extraction', 'hypothesis generation', 'experiment design', 'literature synthesis'],
    quickPrompts: [
      'Break down this paper into key claims',
      'Generate 3 testable hypotheses from this finding',
      'What are the limitations of this approach?',
      'How does this relate to existing literature?',
    ],
    defaultSystemPrompt: `You are a Research Paper Agent on Jorvex.
You help with literature synthesis, paper decomposition, hypothesis generation, and experiment design.
Be intellectually rigorous. Actively look for evidence that contradicts the hypothesis.
Always ask: "what would change my mind?" Generate testable, falsifiable hypotheses.`,
    defaultModel: null,
  },

  ─────────────────────────────────────────────────────────────────────────── */
};

// ── Dynamic entity resolver — reads LocalAgents config at call time ───────────
// Returns entity with live brain/config status merged in
export function getEntity(id) {
  const base = BASE_ENTITIES[id];
  if (!base) return null;

  try {
    const cfgs = JSON.parse(localStorage.getItem('agentConfigs') || '{}');
    const cfg  = cfgs[id];
    if (!cfg) return { ...base, comingSoon: true, configured: false };

    // brainOllamaTag is the actual Ollama model tag (e.g. "qwen3:4b") stored by
    // handleDeploy in LocalAgents. brainId is the legacy internal ID ("qwen3-4b").
    // We always use the Ollama tag for API calls and model-availability checks.
    const brainOllamaTag = cfg.brainOllamaTag || cfg.brainId || null;

    return {
      ...base,
      // Override with user's customisation from Config Studio
      name:         cfg.name         || base.name,
      color:        cfg.color        || base.color,
      description:  cfg.desc         || base.description,
      // Brain — brainId is now the Ollama tag so ChatContext stream call works directly
      brainId:      brainOllamaTag,
      brainName:    cfg.brainName    || cfg.brainId || null,
      // sysPrompt is what Config Studio saves; systemPrompt is the old key — accept both
      systemPrompt: cfg.sysPrompt || cfg.systemPrompt || base.defaultSystemPrompt,
      // personality key was renamed; accept both spellings
      personality:  cfg.personalityCustom || cfg.personality || null,
      goals:        cfg.goals        || null,
      temperature:  cfg.temperature  ?? 0.4,
      chainOfThought: cfg.chainOfThought ?? true,
      selfReflect:  cfg.selfReflect  ?? false,
      memoryMode:   cfg.memoryMode   || 'session',
      ragEnabled:   cfg.ragEnabled   || false,
      // Status
      comingSoon:   !brainOllamaTag,  // live once a valid Ollama tag is assigned
      configured:   true,
      online:       true,
    };
  } catch (_) {
    return { ...base, comingSoon: true, configured: false };
  }
}

export function getEntities() {
  return Object.keys(BASE_ENTITIES).reduce((acc, id) => {
    acc[id] = getEntity(id);
    return acc;
  }, {});
}

export function getEntityGroups() {
  const all      = Object.values(getEntities());
  const myAIs    = all.filter(e => e.type === ENTITY_TYPES.MY_AI);
  const publicAIs= all.filter(e => e.type === ENTITY_TYPES.PUBLIC_AI);
  const humans   = all.filter(e => e.type === ENTITY_TYPES.HUMAN);
  return { myAIs, publicAIs, humans };
}

// ── Platform context builder ──────────────────────────────────────────────────
// Reads window.__dd_ctx + localStorage to give each agent awareness of
// what the user is currently working on.
export function buildPlatformContext() {
  const lines = [];

  try {
    // Real-time context from active module
    const rt = window.__dd_ctx;
    if (rt) {
      if (rt.datasetName)  lines.push(`ACTIVE DATASET: ${rt.datasetName}`);
      if (rt.rowCount)     lines.push(`  ${rt.rowCount} rows × ${rt.colCount} cols`);
      if (rt.qualityScore) lines.push(`  quality_score=${rt.qualityScore}/100`);
      if (rt.topInsights)  lines.push(`  top_insights: ${rt.topInsights.slice(0,2).join(' | ')}`);
      if (rt.currentTab)   lines.push(`  current_tab=${rt.currentTab}`);
      if (rt.module)       lines.push(`  module=${rt.module}`);
    }

    // Active page
    const urlParams = new URLSearchParams(window.location.search);
    const module = urlParams.get('module');
    if (module && !rt?.module) lines.push(`CURRENT_PAGE: ${module}`);

    // Workspace metadata (dataset quality, experiment info)
    const wsKeys = Object.keys(localStorage).filter(k => k.startsWith('dd_workspace_'));
    for (const key of wsKeys.slice(0, 1)) {
      const ws = JSON.parse(localStorage.getItem(key) || '{}');
      const autoItems = ws?.modules?.auto_eda?.items || [];
      if (autoItems.length > 0) {
        const latest = autoItems[autoItems.length - 1];
        if (latest.meta) {
          lines.push(`LAST_EDA: ${latest.title}`);
          if (latest.meta.qualityScore) lines.push(`  quality=${latest.meta.qualityScore}/100, nulls=${latest.meta.nullPct}%`);
        }
      }
    }
  } catch (_) {}

  return lines.length > 0
    ? '\n\n── PLATFORM CONTEXT ──\n' + lines.join('\n') + '\n──────────────────────'
    : '';
}

// ── Ollama streaming client ───────────────────────────────────────────────────
const OLLAMA_BASE = 'http://localhost:11434';

export async function checkOllamaHealth() {
  try {
    const res = await fetch(`${OLLAMA_BASE}/api/tags`, {
      signal: AbortSignal.timeout(2000),
    });
    if (!res.ok) return { ok: false, models: [] };
    const data = await res.json();
    return { ok: true, models: (data.models || []).map(m => m.name) };
  } catch (_) {
    return { ok: false, models: [] };
  }
}

// Async generator — yields tokens as they stream
export async function* streamOllamaChat({ model, messages, temperature = 0.4, signal }) {
  const res = await fetch(`${OLLAMA_BASE}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal,
    body: JSON.stringify({
      model,
      messages,
      stream: true,
      options: { temperature, num_predict: 2048 },
    }),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`Ollama ${res.status}: ${text}`);
  }

  const reader  = res.body.getReader();
  const decoder = new TextDecoder();
  let   buf     = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop();
    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const obj = JSON.parse(line);
        if (obj.message?.content) yield obj.message.content;
        if (obj.done) return;
      } catch (_) {}
    }
  }
}

// ── Build full Ollama message array for a conversation ────────────────────────
export function buildOllamaMessages(entity, history, userText) {
  const platformCtx = buildPlatformContext();

  // System prompt: configured > default
  let sysContent = entity.systemPrompt || entity.defaultSystemPrompt || '';

  // Inject personality + goals from Config Studio if set
  if (entity.personality) {
    sysContent += `\n\nPersonality: ${entity.personality}`;
  }
  if (entity.goals) {
    sysContent += `\nGoals: ${entity.goals}`;
  }

  // Chain of thought instruction
  if (entity.chainOfThought) {
    sysContent += '\n\nThink step by step before answering complex questions.';
  }
  if (entity.selfReflect) {
    sysContent += '\nBriefly critique your own answer before finalising it.';
  }

  // Inject live platform context
  sysContent += platformCtx;

  const ollamaMessages = [
    { role: 'system', content: sysContent },
  ];

  // Include conversation history (last 12 exchanges)
  const recent = history.filter(m => !m.typing && m.from !== 'system').slice(-12);
  for (const msg of recent) {
    ollamaMessages.push({
      role:    msg.from === 'me' ? 'user' : 'assistant',
      content: msg.text,
    });
  }

  // Current user message
  ollamaMessages.push({ role: 'user', content: userText });

  return ollamaMessages;
}

// ── Utility helpers ───────────────────────────────────────────────────────────
export function formatTime(ts) {
  const diff = Date.now() - ts;
  if (diff < 60000)    return 'just now';
  if (diff < 3600000)  return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function getLastMessage(convHistory) {
  if (!convHistory || convHistory.length === 0) return null;
  return convHistory[convHistory.length - 1];
}

// Initial greeting messages — shown when you first open a chat
export function getWelcomeMessage(entityId) {
  const entity = getEntity(entityId);
  if (!entity) return null;

  if (entity.comingSoon) {
    return {
      id: 'welcome',
      from: entityId,
      text: `Hey! I'm ${entity.name}. I'm not quite live yet — head to **Local AI Agents** to configure me with a brain model, then I'll be ready to help with your research.`,
      ts: Date.now() - 1000,
      status: 'read',
      isWelcome: true,
    };
  }

  return {
    id: 'welcome',
    from: entityId,
    text: `Hey! I'm ${entity.name} — running on **${entity.brainName || entity.brainId}**. ${entity.description?.split('.')[0]}. What are you working on?`,
    ts: Date.now() - 1000,
    status: 'read',
    isWelcome: true,
  };
}

// Offline fallback response (when Ollama isn't running)
export function getOfflineResponse(entityId) {
  const entity = getEntity(entityId);
  const name   = entity?.name || 'this agent';
  return `I'm **${name}** and I'm configured, but my Ollama engine isn't reachable right now.

To bring me online:
\`\`\`
ollama serve
\`\`\`
Then make sure **${entity?.brainId || 'a model'}** is pulled:
\`\`\`
ollama pull ${entity?.brainId || 'qwen3:4b'}
\`\`\`
Once Ollama is running, I'll respond with full intelligence.`;
}

// Re-export legacy ENTITIES shape for backwards compat
// (any component that still does `import { ENTITIES } from './chatStore'` keeps working)
export const ENTITIES = new Proxy({}, {
  get(_, id) { return getEntity(String(id)); },
  has(_, id) { return !!BASE_ENTITIES[String(id)]; },
  ownKeys()  { return Object.keys(BASE_ENTITIES); },
  getOwnPropertyDescriptor() { return { enumerable: true, configurable: true }; },
});

export const MOCK_CONVERSATIONS = {};
export const AI_RESPONSES = {};
